import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/authService';
import { colors } from '../styles/colors';
import { useDebounce, useAbortController } from '../hooks/useDebounce';

interface AddRecruiterFormProps {
  companyId: number;
  onSave: () => void;
  onCancel: () => void;
}

const AddRecruiterForm: React.FC<AddRecruiterFormProps> = ({ companyId, onSave, onCancel }) => {
  const [searchType, setSearchType] = useState('name');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedRecruiter, setSelectedRecruiter] = useState<any>(null);
  const [searching, setSearching] = useState(false);
  const [formData, setFormData] = useState({
    owner: false,
    active: true,
    pending: false,
    publish_opportunity: false,
    candidates_database_access: false,
  });
  const [loading, setLoading] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;

    setSearching(true);
    try {
      const params = new URLSearchParams({
        search: searchTerm,
        searchType: searchType
      });
      const response = await api.get(`/recruiter-list?${params}`);
      setSearchResults(response.data.recruiters || []);
      if (response.data.recruiters.length === 0) {
        toast.info('Nenhum recrutador encontrado');
      }
    } catch (error) {
      toast.error('Erro ao buscar recrutador');
    } finally {
      setSearching(false);
    }
  };

  const handleSelectRecruiter = (recruiter: any) => {
    setSelectedRecruiter(recruiter);
    setSearchResults([]);
    setSearchTerm('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedRecruiter) {
      toast.error('Selecione um recrutador');
      return;
    }

    setLoading(true);

    try {
      await api.post(`/companies/${companyId}/recruiters`, {
        recruiter_id: selectedRecruiter.id,
        ...formData
      });
      toast.success('Recrutador adicionado com sucesso!');
      onSave();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao adicionar recrutador');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Adicionar Recrutador</h2>
          <button onClick={onCancel} style={styles.closeBtn} type="button">×</button>
        </div>
        
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.searchSection}>
            <h3 style={styles.sectionTitle}>Buscar Recrutador</h3>
            <div style={styles.searchForm}>
              <select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                style={styles.select}
              >
                <option value="name">Nome</option>
                <option value="email">Email</option>
                <option value="id">ID</option>
              </select>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearch(e as any);
                  }
                }}
                placeholder={`Buscar por ${searchType === 'id' ? 'ID' : searchType === 'email' ? 'email' : 'nome'}...`}
                style={styles.searchInput}
              />
              <button
                type="button"
                onClick={handleSearch}
                disabled={searching}
                style={styles.searchBtn}
              >
                {searching ? 'Buscando...' : 'Buscar'}
              </button>
            </div>
            
            {searchResults.length > 0 && (
              <div style={styles.searchResults}>
                {searchResults.map((rec) => (
                  <div
                    key={rec.id}
                    onClick={() => handleSelectRecruiter(rec)}
                    style={styles.searchResultItem}
                  >
                    <strong>{rec.name || rec.email}</strong>
                    <div style={styles.searchResultDetails}>
                      ID: {rec.id} | Email: {rec.email}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {selectedRecruiter && (
              <div style={styles.selectedRecruiter}>
                <strong>Recrutador Selecionado:</strong>
                <div>{selectedRecruiter.name || selectedRecruiter.email}</div>
                <div style={styles.searchResultDetails}>
                  ID: {selectedRecruiter.id} | Email: {selectedRecruiter.email}
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedRecruiter(null)}
                  style={styles.removeBtn}
                >
                  Remover Seleção
                </button>
              </div>
            )}
          </div>

          {selectedRecruiter && (
            <>
              <h3 style={styles.sectionTitle}>Recrutador</h3>

              <div style={styles.checkboxGroup}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formData.owner}
                    onChange={(e) => handleChange('owner', e.target.checked)}
                  />
                  Owner
                </label>
              </div>

              <div style={styles.checkboxGroup}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => handleChange('active', e.target.checked)}
                  />
                  Ativo
                </label>
              </div>

              <div style={styles.checkboxGroup}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formData.pending}
                    onChange={(e) => handleChange('pending', e.target.checked)}
                  />
                  Pendente
                </label>
              </div>

              <h3 style={styles.sectionTitle}>Permissões</h3>

              <div style={styles.checkboxGroup}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formData.publish_opportunity}
                    onChange={(e) => handleChange('publish_opportunity', e.target.checked)}
                  />
                  Publicar Oportunidade
                </label>
              </div>

              <div style={styles.checkboxGroup}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={formData.candidates_database_access}
                    onChange={(e) => handleChange('candidates_database_access', e.target.checked)}
                  />
                  Base de Candidatos
                </label>
              </div>
            </>
          )}
          
          <div style={styles.buttons}>
            <button
              type="button"
              onClick={onCancel}
              style={styles.cancelBtn}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !selectedRecruiter}
              style={styles.saveBtn}
            >
              {loading ? 'Salvando...' : 'Adicionar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1001,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem 2rem 0 2rem',
  },
  title: {
    margin: 0,
    color: colors.primary,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    cursor: 'pointer',
    color: colors.textLight,
    padding: 0,
    lineHeight: 1,
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    padding: '0 2rem 2rem 2rem',
  },
  searchSection: {
    marginBottom: '1.5rem',
    marginTop: '1rem',
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    color: colors.primary,
    marginTop: '1.5rem',
    marginBottom: '1rem',
    borderBottom: `2px solid ${colors.primary}`,
    paddingBottom: '0.5rem',
  },
  searchForm: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  select: {
    padding: '0.5rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '0.9rem',
  },
  searchInput: {
    flex: 1,
    padding: '0.5rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '0.9rem',
  },
  searchBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  searchResults: {
    maxHeight: '200px',
    overflowY: 'auto' as const,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    marginBottom: '1rem',
  },
  searchResultItem: {
    padding: '0.75rem',
    borderBottom: `1px solid ${colors.border}`,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  searchResultDetails: {
    fontSize: '0.85rem',
    color: colors.textLight,
    marginTop: '0.25rem',
  },
  selectedRecruiter: {
    padding: '1rem',
    backgroundColor: '#e8f5e9',
    border: `1px solid #4caf50`,
    borderRadius: '4px',
  },
  removeBtn: {
    marginTop: '0.5rem',
    padding: '0.25rem 0.5rem',
    backgroundColor: colors.error,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.8rem',
  },
  checkboxGroup: {
    marginBottom: '1rem',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: colors.text,
    cursor: 'pointer',
    fontSize: '1rem',
  },
  buttons: {
    display: 'flex',
    gap: '1rem',
    marginTop: '1.5rem',
  },
  cancelBtn: {
    flex: 1,
    padding: '0.75rem',
    backgroundColor: colors.border,
    color: colors.text,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '1rem',
  },
  saveBtn: {
    flex: 1,
    padding: '0.75rem',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '1rem',
  },
};

export default AddRecruiterForm;
